/**
 * Display-safe company name resolver.
 *
 * Signup creates a placeholder company in the backend with a name of
 * the form `__pending__<email>` (see backend/routes/auth.js — both the
 * password signup and Google sign-in paths use this). The user is
 * supposed to land on the profile-completion modal next and pick a
 * real company name, which renames the row away from the placeholder.
 *
 * BUT if the user closes the modal, dismisses it, or signs in via
 * Google without ever completing profile setup, the placeholder leaks
 * to every surface that renders `authUser.companyName` raw — top bar,
 * dashboard welcome, settings tooltips, follow-up email templates,
 * etc. Customers see "__pending__fellowsgomez@gmail.com" plastered
 * across the app and it looks broken.
 *
 * This helper sanitizes the value for display. It returns:
 *   - The provided fallback when the raw name is missing OR
 *     starts with __pending__ (the canonical placeholder prefix)
 *   - The raw name otherwise
 *
 * Caller picks the right fallback for their surface:
 *   - TopBar:          'TrailerCast' (product name fallback)
 *   - Welcome message: null + the caller hides the suffix
 *   - Settings:        'Your company' or similar
 *
 * Single source of truth so every renderer behaves consistently and
 * a future schema change (e.g. tracking placeholder state via a
 * boolean column instead of a name prefix) only needs to update this
 * file.
 */

export function displayCompanyName(raw, fallback = null) {
  if (!raw || typeof raw !== 'string') return fallback;
  if (raw.startsWith('__pending__')) return fallback;
  return raw;
}

export function isPlaceholderCompanyName(raw) {
  return typeof raw === 'string' && raw.startsWith('__pending__');
}
